import React, { useState, useEffect } from 'react';
import './Admin.css';
import { auditFetch } from '../auditFetch';
import { usePermissions, ALL_PERMISSIONS } from '../context/PermissionsContext';
import Qualifications from './Qualifications';
import AdminSms from './AdminSms';
import AdminBCIntegration from './AdminBCIntegration';
import AdminCompetitionMapping from './AdminCompetitionMapping';
import AdminErrorLog from './AdminErrorLog';
import AdminRefereeCodes from './AdminRefereeCodes';
import AdminAIManagement from './AdminAIManagement';
import AdminReporting from './AdminReporting';
import CoachingProgramForm from '../components/CoachingProgramForm';
import AdminSwishIq from './AdminSwishIq';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

// Map admin tab keys to their security section keys.
// Currently unreferenced in render code (each tab button checks
// canView() inline) — kept here as a single source of truth for the
// list of admin sections in case a future refactor needs to iterate.
// eslint-disable-next-line no-unused-vars
const ADMIN_TAB_PERMISSION_MAP = {
  'games': 'games',
  'settings': 'settings',
  'budget': 'budget',
  'audit': 'audit',
  'program-types': 'program-types',
  'competition-mapping': 'competition-mapping',
  'error-log': 'error-log',
  'referee-codes': 'referee-codes',
  'coach-codes': 'coach-codes',
  'swishiq': 'settings',
  'security-profiles': 'security-profiles',
  'site-permissions': 'site-permissions',
  'roster-response': 'roster-response',
  'business-banking': 'business-banking',
  'sms': 'sms',
  'ai-management': 'ai-management',
};

export default function Admin() {
  const { canView, canEdit } = usePermissions();
  const [activeTab, setActiveTab] = useState('');
  const [games, setGames] = useState([]);
  const [gameFormOptions, setGameFormOptions] = useState({ courts: [], competitions: [], ageGroups: [], divisions: [] });
  const [gameTimeOffset, setGameTimeOffset] = useState(11);
  const [payVisibleToStaff, setPayVisibleToStaff] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Audit state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize] = useState(50);
  const [auditCategory, setAuditCategory] = useState('');
  const [auditLoading, setAuditLoading] = useState(false);

  const [gameForm, setGameForm] = useState({ matchId: '', team1: '', team2: '', scheduledTime: '', court: '', division: '' });
  const [importLoading, setImportLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  // Default the picker to start of current calendar year — matches the cron importer's default.
  const [syncAfterDate, setSyncAfterDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [gameSearch, setGameSearch] = useState('');

  // State for daily budgets (Monday-Sunday)
  const [budgets, setBudgets] = useState([]);
  const [budgetSaveStatus, setBudgetSaveStatus] = useState('');

  // Security profiles state
  const [securityProfiles, setSecurityProfiles] = useState([]);
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', permissions: {} });
  const [userPermissions, setUserPermissions] = useState([]);
  const [userPermForm, setUserPermForm] = useState({ userEmail: '', securityProfileId: '' });

  // Program Types state
  const [programTypes, setProgramTypes] = useState([]);
  const [editingProgramTypeId, setEditingProgramTypeId] = useState(null);

  // Coaching Courts state
  const [coachingCourts, setCoachingCourts] = useState([]);
  const [courtForm, setCourtForm] = useState({ name: '' });
  const [editingCourtId, setEditingCourtId] = useState(null);
  const [showCourtForm, setShowCourtForm] = useState(false);

  // (Weekly Templates state removed — manual templates are deprecated.
  //  BC integration mapping creates the underlying template + sessions.)
  // DAY_NAMES + participants removed (only used by the Weekly Templates form).
  // The coaching program form (people, coaches, schedule) lives in
  // components/CoachingProgramForm, shared with BC Registrations.

  // Modal visibility state
  const [showGameForm, setShowGameForm] = useState(false);
  const [showProgramTypeForm, setShowProgramTypeForm] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showPermissionForm, setShowPermissionForm] = useState(false);

  // Archive toggle state
  const [showArchivedCourts, setShowArchivedCourts] = useState(false);
  const [showArchivedProgramTypes, setShowArchivedProgramTypes] = useState(false);
  const [showArchivedProfiles, setShowArchivedProfiles] = useState(false);

  // Roster rejection state
  const [rosterCutoff, setRosterCutoff] = useState({ game: 24, coach: 24, court: 24, gameEnabled: true, coachEnabled: true, courtEnabled: true });

  // Business banking state
  const [businessBanking, setBusinessBanking] = useState({
    bsbNumber: '', accountNumber: '', accountName: '',
    financialInstitutionCode: '', lodgementReference: '',
    nameOfUserSupplyingFile: 'Illawarra Basketball Association',
    apcaNumber: ''
  });

  // No auto-selected sub-tab on first load — Admin sub-tabs each fetch
  // their own data (some are slow, e.g. Games loads every game), so
  // jumping straight to the first one wastes the load if the user
  // actually wanted a different tab. They click the tab they want and
  // we load it then.

  // Load data on tab click + when an archive toggle changes. The bail-out
  // when activeTab is empty matters — Admin loads quite a lot in parallel
  // (games / settings / budget / form-options / pay-visibility / etc.)
  // and we don't want that firing on every visit before the user has
  // even picked which sub-tab they care about.
  useEffect(() => {
    if (!activeTab) return;
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showArchivedCourts, showArchivedProgramTypes, showArchivedProfiles]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [gamesRes, offsetRes, budgetRes, formOptionsRes, payVisRes] = await Promise.all([
        fetch(`${API_BASE}/games`),
        fetch(`${API_BASE}/settings/game-time-offset`),
        fetch(`${API_BASE}/settings/budget`),
        fetch(`${API_BASE}/games/form-options`),
        fetch(`${API_BASE}/settings/pay-visibility`)
      ]);

      if (gamesRes.ok) setGames(await gamesRes.json());
      if (offsetRes.ok) {
        const data = await offsetRes.json();
        setGameTimeOffset(data.offset);
      }
      if (payVisRes.ok) {
        const data = await payVisRes.json();
        setPayVisibleToStaff(data.payVisibleToStaff);
      }
      if (budgetRes.ok) {
        const data = await budgetRes.json();
        setBudgets(data);
      }
      if (formOptionsRes.ok) {
        const data = await formOptionsRes.json();
        setGameFormOptions(data);
      }

      // Load security data
      try {
        const profilesUrl = showArchivedProfiles ? `${API_BASE}/security-profiles?includeArchived=true` : `${API_BASE}/security-profiles`;
        const [profilesRes, userPermsRes] = await Promise.all([
          fetch(profilesUrl),
          fetch(`${API_BASE}/user-permissions`)
        ]);
        if (profilesRes.ok) setSecurityProfiles(await profilesRes.json());
        if (userPermsRes.ok) setUserPermissions(await userPermsRes.json());
      } catch (secErr) {
        console.error('Failed to load security data:', secErr);
      }

      // Load program types and courts.
      // (participants fetch removed — only consumed by the now-deleted
      //  Weekly Templates form.)
      try {
        const ptUrl = showArchivedProgramTypes ? `${API_BASE}/program-types?includeArchived=true` : `${API_BASE}/program-types`;
        const courtsUrl = showArchivedCourts ? `${API_BASE}/coaching-courts?includeArchived=true` : `${API_BASE}/coaching-courts`;
        const [ptRes, courtsRes] = await Promise.all([
          fetch(ptUrl),
          fetch(courtsUrl)
        ]);
        if (ptRes.ok) setProgramTypes(await ptRes.json());
        if (courtsRes.ok) setCoachingCourts(await courtsRes.json());
      } catch (ptErr) {
        console.error('Failed to load program data:', ptErr);
      }

      // Load roster cutoff settings
      try {
        const cutoffRes = await fetch(`${API_BASE}/settings/roster-cutoff`);
        if (cutoffRes.ok) setRosterCutoff(await cutoffRes.json());
      } catch (cutoffErr) {
        console.error('Failed to load roster cutoff settings:', cutoffErr);
      }

      // Load business banking settings
      try {
        const bizBankRes = await fetch(`${API_BASE}/settings/business-banking`);
        if (bizBankRes.ok) setBusinessBanking(await bizBankRes.json());
      } catch (bizErr) {
        console.error('Failed to load business banking settings:', bizErr);
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== SECURITY PROFILES ====================

  const initProfilePermissions = () => {
    const perms = {};
    ALL_PERMISSIONS.forEach(p => {
      if (!perms[p.pageKey]) perms[p.pageKey] = {};
      perms[p.pageKey][p.sectionKey] = 'hide';
    });
    return perms;
  };

  const handleAddProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingProfileId ? `${API_BASE}/security-profiles/${editingProfileId}` : `${API_BASE}/security-profiles`;
      const method = editingProfileId ? 'PUT' : 'POST';

      const permissions = [];
      Object.entries(profileForm.permissions).forEach(([pageKey, sections]) => {
        Object.entries(sections).forEach(([sectionKey, level]) => {
          permissions.push({ pageKey, sectionKey, permissionLevel: level });
        });
      });

      const response = await auditFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: profileForm.name, permissions })
      });

      if (response.ok) {
        setSuccess(editingProfileId ? 'Security profile updated!' : 'Security profile created!');
        setProfileForm({ name: '', permissions: {} });
        setEditingProfileId(null);
        setShowProfileForm(false);
        loadAllData();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to save security profile');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleEditProfile = (profile) => {
    const perms = initProfilePermissions();
    if (profile.permissions) {
      profile.permissions.forEach(p => {
        if (perms[p.pageKey]) {
          perms[p.pageKey][p.sectionKey] = p.permissionLevel;
        }
      });
    }
    setProfileForm({ name: profile.name, permissions: perms });
    setEditingProfileId(profile.id);
    setShowProfileForm(true);
  };

  const handleArchiveProfile = async (id) => {
    if (!window.confirm('Archive this security profile? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/security-profiles/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Security profile archived!');
        loadAllData();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to archive profile');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleAssignPermission = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await auditFetch(`${API_BASE}/user-permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: userPermForm.userEmail,
          securityProfileId: parseInt(userPermForm.securityProfileId)
        })
      });

      if (response.ok) {
        setSuccess('User permission assigned!');
        setUserPermForm({ userEmail: '', securityProfileId: '' });
        setShowPermissionForm(false);
        loadAllData();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to assign permission');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleRemoveUserPermission = async (email) => {
    if (!window.confirm(`Remove access for ${email}?`)) return;
    try {
      const response = await auditFetch(`${API_BASE}/user-permissions/${encodeURIComponent(email)}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('User permission removed!');
        loadAllData();
      } else {
        setError('Failed to remove user permission');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleChangeUserProfile = async (email, profileId) => {
    try {
      const response = await auditFetch(`${API_BASE}/user-permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: email, securityProfileId: parseInt(profileId) })
      });
      if (response.ok) {
        setSuccess('User profile updated!');
        loadAllData();
      } else {
        setError('Failed to update user profile');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== COACHING COURTS ====================

  const handleAddCourt = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const url = editingCourtId ? `${API_BASE}/coaching-courts/${editingCourtId}` : `${API_BASE}/coaching-courts`;
      const method = editingCourtId ? 'PUT' : 'POST';
      const response = await auditFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: courtForm.name })
      });
      if (response.ok) {
        setSuccess(editingCourtId ? 'Court updated!' : 'Court added!');
        setCourtForm({ name: '' });
        setEditingCourtId(null);
        setShowCourtForm(false);
        loadAllData();
      } else {
        setError('Failed to save court');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleArchiveCourt = async (id) => {
    if (!window.confirm('Archive this court? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/coaching-courts/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Court archived!');
        loadAllData();
      } else {
        setError('Failed to archive court');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== PROGRAM TYPES ====================

  const handleEditProgramType = (pt) => {
    setEditingProgramTypeId(pt.id);
    setShowProgramTypeForm(true);
  };

  const handleArchiveProgramType = async (id) => {
    if (!window.confirm('Archive this program type? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/program-types/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Program type archived!');
        loadAllData();
      } else {
        setError('Failed to archive program type');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // (Weekly Templates handlers removed — section is deprecated.)

  const setPermLevel = (pageKey, sectionKey, level) => {
    setProfileForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [pageKey]: {
          ...prev.permissions[pageKey],
          [sectionKey]: level
        }
      }
    }));
  };

  // Save pay visibility setting
  const handleSavePayVisibility = async (visible) => {
    try {
      const response = await auditFetch(`${API_BASE}/settings/pay-visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payVisibleToStaff: visible })
      });
      if (response.ok) {
        setPayVisibleToStaff(visible);
        setSuccess(`Pay visibility ${visible ? 'enabled' : 'disabled'} for staff portal`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save pay visibility setting');
      }
    } catch (err) {
      setError('Error saving pay visibility: ' + err.message);
    }
  };

  // Save game time offset
  const handleSaveGameTimeOffset = async () => {
    try {
      const response = await auditFetch(`${API_BASE}/settings/game-time-offset`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offset: parseInt(gameTimeOffset) })
      });

      if (response.ok) {
        setSuccess('Game time offset saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save game time offset');
      }
    } catch (err) {
      setError('Error saving game time offset: ' + err.message);
    }
  };

  // Save daily budgets
  const handleSaveBudgets = async () => {
    try {
      setBudgetSaveStatus('saving...');
      const response = await auditFetch(`${API_BASE}/settings/budget`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budgets })
      });

      if (response.ok) {
        setSuccess('Budgets saved successfully!');
        setBudgetSaveStatus('');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save budgets');
        setBudgetSaveStatus('');
      }
    } catch (err) {
      setError('Error saving budgets: ' + err.message);
      setBudgetSaveStatus('');
    }
  };

  // ==================== GAMES ====================

  const handleAddGame = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await auditFetch(`${API_BASE}/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchId: gameForm.matchId,
          team1: gameForm.team1,
          team2: gameForm.team2,
          scheduledTime: new Date(gameForm.scheduledTime).toISOString(),
          court: gameForm.court,
          competition: 'Basketball',
          ageGroup: 'Manual',
          division: gameForm.division
        })
      });

      if (response.ok) {
        setSuccess('Game added successfully!');
        setGameForm({ matchId: '', team1: '', team2: '', scheduledTime: '', court: '', division: '' });
        setShowGameForm(false);
        loadAllData();
      } else {
        setError('Failed to add game');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleForceImport = async () => {
    setError('');
    setSuccess('');
    setImportLoading(true);
    try {
      const response = await auditFetch(`${API_BASE}/games/import`, {
        method: 'POST'
      });
      if (response.ok) {
        setSuccess('Game import completed successfully! Games have been refreshed.');
        loadAllData();
      } else {
        const data = await response.json();
        setError(data.error || 'Game import failed');
      }
    } catch (err) {
      setError('Import error: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleRepairTeamNames = async () => {
    setError('');
    setSuccess('');
    setRepairResult(null);
    setRepairLoading(true);
    try {
      const response = await auditFetch(`${API_BASE}/games/repair-team-names`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        setRepairResult(data);
        setSuccess(`Inspected ${data.inspected} game(s) with placeholder names — repaired ${data.repaired}.`);
        loadAllData();
      } else {
        setError(data.error || 'Repair failed');
      }
    } catch (err) {
      setError('Repair error: ' + err.message);
    } finally {
      setRepairLoading(false);
    }
  };

  const handleSyncFromUpstream = async (afterDate) => {
    setSyncMessage('');
    setSyncLoading(true);
    try {
      const response = await auditFetch(`${API_BASE}/team-overrides/sync-from-upstream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(afterDate ? { afterDate } : {})
      });
      const data = await response.json();
      if (!response.ok) {
        const parts = [data.error || response.status];
        if (data.upstreamStatus != null) parts.push(`upstream HTTP ${data.upstreamStatus}`);
        if (data.upstreamBody) parts.push(`body: ${data.upstreamBody}`);
        if (data.hint) parts.push(data.hint);
        setSyncMessage('Failed — ' + parts.join(' · '));
        return;
      }
      setSyncMessage(`Synced ${data.saved} team(s) from upstream. Re-running repair…`);
      handleRepairTeamNames();
    } catch (err) {
      setSyncMessage('Sync error: ' + err.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const loadAuditLogs = async (page, category) => {
    setAuditLoading(true);
    try {
      const cat = category !== undefined ? category : auditCategory;
      const url = `${API_BASE}/audit-log?page=${page}&pageSize=${auditPageSize}${cat ? `&category=${encodeURIComponent(cat)}` : ''}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs);
        setAuditTotal(data.total);
        setAuditPage(page);
      } else {
        setError('Failed to load audit logs');
      }
    } catch (err) {
      setError('Error loading audit logs: ' + err.message);
    } finally {
      setAuditLoading(false);
    }
  };

  // ==================== ROSTER CUTOFF ====================
  const handleSaveRosterCutoff = async () => {
    try {
      setError('');
      const response = await auditFetch(`${API_BASE}/settings/roster-cutoff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rosterCutoff)
      });
      if (response.ok) {
        setSuccess('Roster cutoff settings saved!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save roster cutoff settings');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== BUSINESS BANKING ====================
  const handleSaveBusinessBanking = async () => {
    try {
      setError('');
      const response = await auditFetch(`${API_BASE}/settings/business-banking`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(businessBanking)
      });
      if (response.ok) {
        setSuccess('Business banking details saved!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save business banking details');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const formatAuditTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="admin">
      <div className="admin-tabs">
        {canView('admin', 'games') && <button className={activeTab === 'games' ? 'active' : ''} onClick={() => setActiveTab('games')}>Games</button>}
        {canView('admin', 'settings') && <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>Settings</button>}
        {canView('admin', 'budget') && <button className={activeTab === 'budget' ? 'active' : ''} onClick={() => setActiveTab('budget')}>Budget</button>}
        {canView('admin', 'audit') && <button className={activeTab === 'audit' ? 'active' : ''} onClick={() => { setActiveTab('audit'); loadAuditLogs(1); }}>Audit</button>}
        {canView('admin', 'error-log') && <button className={activeTab === 'error-log' ? 'active' : ''} onClick={() => setActiveTab('error-log')}>Error Log</button>}
        {canView('admin', 'program-types') && <button className={activeTab === 'coaching-courts' ? 'active' : ''} onClick={() => setActiveTab('coaching-courts')}>Coaching Courts</button>}
        {canView('admin', 'program-types') && <button className={activeTab === 'program-types' ? 'active' : ''} onClick={() => setActiveTab('program-types')}>Coaching Programs</button>}
        {/* Weekly Templates tab removed — see comment further down where the
            section's JSX used to live. */}
        {canView('admin', 'security-profiles') && <button className={activeTab === 'security-profiles' ? 'active' : ''} onClick={() => setActiveTab('security-profiles')}>Security Profiles</button>}
        {canView('admin', 'site-permissions') && <button className={activeTab === 'site-permissions' ? 'active' : ''} onClick={() => setActiveTab('site-permissions')}>Site Permissions</button>}
        {canView('admin', 'roster-response') && <button className={activeTab === 'roster-response' ? 'active' : ''} onClick={() => setActiveTab('roster-response')}>Roster Response</button>}
        {canView('admin', 'business-banking') && <button className={activeTab === 'business-banking' ? 'active' : ''} onClick={() => setActiveTab('business-banking')}>Business Banking</button>}
        {canView('admin', 'sms') && <button className={activeTab === 'sms' ? 'active' : ''} onClick={() => setActiveTab('sms')}>SMS</button>}
        {canView('admin', 'reporting') && <button className={activeTab === 'reporting' ? 'active' : ''} onClick={() => setActiveTab('reporting')}>Reporting</button>}
        {canView('admin', 'settings') && <button className={activeTab === 'bc-integration' ? 'active' : ''} onClick={() => setActiveTab('bc-integration')}>BC Integration</button>}
        {canView('admin', 'competition-mapping') && <button className={activeTab === 'competition-mapping' ? 'active' : ''} onClick={() => setActiveTab('competition-mapping')}>BC Registrations</button>}
        {(canView('admin', 'referee-qualifications') || canView('admin', 'bench-qualifications') || canView('admin', 'court-qualifications') || canView('admin', 'division-age-groups') || canView('admin', 'coach-qualifications')) && <button className={activeTab === 'qualifications' ? 'active' : ''} onClick={() => setActiveTab('qualifications')}>Qualifications</button>}
        {canView('admin', 'referee-codes') && <button className={activeTab === 'referee-codes' ? 'active' : ''} onClick={() => setActiveTab('referee-codes')}>Referee Codes</button>}
        {canView('admin', 'coach-codes') && <button className={activeTab === 'coach-codes' ? 'active' : ''} onClick={() => setActiveTab('coach-codes')}>Coach Codes</button>}
        {canView('admin', 'settings') && <button className={activeTab === 'swishiq' ? 'active' : ''} onClick={() => setActiveTab('swishiq')}>SwishIQ Trials</button>}
        {canView('admin', 'ai-management') && <button className={activeTab === 'ai-management' ? 'active' : ''} onClick={() => setActiveTab('ai-management')}>AI Management</button>}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading && <div className="loading">Loading...</div>}

      {/* Empty state — no sub-tab picked yet */}
      {!activeTab && (
        <div className="admin-section" style={{ padding: '2.5rem 1.5rem', textAlign: 'center', color: '#555' }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Pick an admin section above</h3>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Each section fetches its own data on click, so the page stays snappy. Pick the one you want to work on.
          </p>
        </div>
      )}

      {/* GAMES TAB */}
      {activeTab === 'games' && (
        <div className="admin-section">
          {showGameForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowGameForm(false)}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>Add New Game</h3>
                  <button onClick={() => setShowGameForm(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddGame} className="admin-form">
                  <div className="form-group">
                    <label>Match ID:</label>
                    <input type="text" placeholder="MATCH001" value={gameForm.matchId} onChange={(e) => setGameForm({ ...gameForm, matchId: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Team 1:</label>
                    <input type="text" placeholder="Team A" value={gameForm.team1} onChange={(e) => setGameForm({ ...gameForm, team1: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Team 2:</label>
                    <input type="text" placeholder="Team B" value={gameForm.team2} onChange={(e) => setGameForm({ ...gameForm, team2: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Scheduled Time:</label>
                    <input type="datetime-local" value={gameForm.scheduledTime} onChange={(e) => setGameForm({ ...gameForm, scheduledTime: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Court:</label>
                    <select value={gameForm.court} onChange={(e) => setGameForm({ ...gameForm, court: e.target.value })} required>
                      <option value="">Select Court</option>
                      {gameFormOptions.courts.map(c => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Division:</label>
                    <select value={gameForm.division} onChange={(e) => setGameForm({ ...gameForm, division: e.target.value })} required>
                      <option value="">Select Division</option>
                      {gameFormOptions.divisions.map(d => (<option key={d} value={d}>{d}</option>))}
                    </select>
                  </div>
                  <button type="submit" className="btn-submit">Add Game</button>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setShowGameForm(true)}
                style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
              >+ Add Game</button>
              <button
                onClick={handleForceImport}
                disabled={importLoading}
                style={{
                  padding: '0.5rem 1rem',
                  background: importLoading ? '#999' : '#A4161A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 600,
                  cursor: importLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                {importLoading ? 'Importing...' : 'Force Import Games'}
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder="Search by team, date, court, or division..."
            value={gameSearch}
            onChange={(e) => setGameSearch(e.target.value)}
            style={{ width: '100%', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
          />
          <table className="admin-table">
            <thead><tr><th>Match ID</th><th>Teams</th><th>Time</th><th>Court</th><th>Division</th></tr></thead>
            <tbody>
              {games.filter(game => {
                if (!gameSearch) return true;
                const search = gameSearch.toLowerCase();
                const timeStr = new Date(game.scheduledTime).toLocaleString('en-AU').toLowerCase();
                return (game.team1 || '').toLowerCase().includes(search) ||
                       (game.team2 || '').toLowerCase().includes(search) ||
                       (game.matchId || '').toLowerCase().includes(search) ||
                       (game.court || '').toLowerCase().includes(search) ||
                       (game.division || '').toLowerCase().includes(search) ||
                       timeStr.includes(search);
              }).map(game => (
                <tr key={game.id}>
                  <td>{game.matchId}</td>
                  <td>{game.team1} vs {game.team2}</td>
                  <td>{new Date(game.scheduledTime).toLocaleString('en-AU')}</td>
                  <td>{game.court}</td>
                  <td>{game.division}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SETTINGS TAB */}
      {activeTab === 'settings' && (
        <div className="admin-section">
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Adjust the timezone offset for displaying game times. This is useful for daylight saving time changes.
              <br />
              Current timezone: UTC{gameTimeOffset >= 0 ? '+' : ''}{gameTimeOffset}
            </p>
            <div className="form-group">
              <label>Game Time Offset (hours from UTC):</label>
              <select
                value={gameTimeOffset}
                onChange={(e) => setGameTimeOffset(parseInt(e.target.value))}
                style={{ padding: '10px', fontSize: '16px', borderRadius: '4px', border: '1px solid #ddd', width: '200px' }}
              >
                {Array.from({ length: 25 }, (_, i) => {
                  const offset = -12 + i;
                  return (<option key={offset} value={offset}>UTC{offset >= 0 ? '+' : ''}{offset}</option>);
                })}
              </select>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                Examples:<br/>
                • Sydney AEDT (summer, Oct-Apr): +11<br/>
                • Sydney AEST (winter, Apr-Oct): +10<br/>
                • UTC: 0<br/>
                • London: 0 (GMT) or +1 (BST)
              </p>
            </div>
            <button onClick={handleSaveGameTimeOffset} className="btn-submit" style={{ marginTop: '20px' }}>
              Save Game Time Offset
            </button>

            <hr style={{ margin: '30px 0', borderColor: '#eee' }} />

            <h3 style={{ marginBottom: '15px' }}>Pay Visibility</h3>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Control whether staff can see pay information in their portal and roster emails.
            </p>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '1rem' }}>
                <input
                  type="checkbox"
                  checked={payVisibleToStaff}
                  onChange={(e) => handleSavePayVisibility(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                Show pay information to staff in their portal and emails
              </label>
              <p style={{ marginTop: '8px', fontSize: '13px', color: '#999' }}>
                When unchecked, all pay amounts, forecast pay, and attended pay will be hidden from the staff portal and roster emails.
              </p>
            </div>

            <hr style={{ margin: '30px 0', borderColor: '#eee' }} />

            <h3 style={{ marginBottom: '15px' }}>Sync Teams from Upstream</h3>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Pulls teams updated after the chosen date from <code>/livescores/teams</code> and rewrites any games
              in the database that still show a <code>Team {'{id}'}</code> placeholder. Pick the smallest window that
              still includes the teams you need — smaller windows respond faster. The call can take 30–90 seconds.
            </p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label htmlFor="sync-after-date" style={{ display: 'block', fontSize: '13px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>
                  Updated after
                </label>
                <input
                  id="sync-after-date"
                  type="date"
                  value={syncAfterDate}
                  onChange={e => setSyncAfterDate(e.target.value)}
                  disabled={syncLoading || repairLoading}
                  max={new Date().toISOString().split('T')[0]}
                  style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }}
                />
              </div>
              <button
                onClick={() => handleSyncFromUpstream(syncAfterDate)}
                disabled={syncLoading || repairLoading || !syncAfterDate}
                className="btn-submit"
                style={{
                  background: (syncLoading || repairLoading) ? '#999' : '#2e7d32',
                  cursor: (syncLoading || repairLoading) ? 'not-allowed' : 'pointer'
                }}
              >
                {syncLoading ? 'Syncing…' : repairLoading ? 'Repairing…' : 'Sync Teams'}
              </button>
              {syncMessage && <div style={{ fontSize: '13px', color: '#444', flex: '1 1 100%', wordBreak: 'break-word' }}>{syncMessage}</div>}
            </div>

            {repairResult && (
              <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f5f7fa', border: '1px solid #d0d7de', borderRadius: '6px', fontSize: '14px' }}>
                Inspected <strong>{repairResult.inspected}</strong> placeholder game(s) — repaired <strong>{repairResult.repaired}</strong>.
              </div>
            )}
          </div>
        </div>
      )}

      {/* BUDGET TAB */}
      {activeTab === 'budget' && (
        <div className="admin-section">
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Set the budget limit for each day of the week. This budget will be used to track game costs against spending limits.
          </p>
          <table className="admin-table" style={{ marginBottom: '20px' }}>
            <thead>
              <tr><th>Day of Week</th><th>Budget Amount</th><th>Alert Threshold</th></tr>
            </thead>
            <tbody>
              {budgets.map((budget) => (
                <tr key={budget.dayOfWeek}>
                  <td><strong>{budget.dayName}</strong></td>
                  <td>
                    <input
                      type="number" step="0.01" min="0" value={budget.budgetAmount}
                      onChange={(e) => {
                        const updated = budgets.map(b => b.dayOfWeek === budget.dayOfWeek ? { ...b, budgetAmount: parseFloat(e.target.value) || 0 } : b);
                        setBudgets(updated);
                      }}
                      style={{ width: '120px', padding: '6px' }}
                    />
                  </td>
                  <td>
                    <select
                      value={budget.alertThresholdPercent}
                      onChange={(e) => {
                        const updated = budgets.map(b => b.dayOfWeek === budget.dayOfWeek ? { ...b, alertThresholdPercent: parseInt(e.target.value) } : b);
                        setBudgets(updated);
                      }}
                      style={{ padding: '6px' }}
                    >
                      <option value="80">80% (Early warning)</option>
                      <option value="100">100% (At limit)</option>
                      <option value="120">120% (Allow over)</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={handleSaveBudgets} className="btn-submit" disabled={budgetSaveStatus === 'saving...'} style={{ width: 'auto', padding: '8px 16px' }}>
            {budgetSaveStatus === 'saving...' ? 'Saving...' : 'Save Budgets'}
          </button>
        </div>
      )}

      {/* AUDIT TAB */}
      {activeTab === 'audit' && (
        <div className="admin-section">
          <p style={{ marginBottom: '15px', color: '#666' }}>View all actions performed in the platform.</p>
          <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label>Filter by category:</label>
            <select value={auditCategory} onChange={(e) => { setAuditCategory(e.target.value); loadAuditLogs(1, e.target.value); }} style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ddd' }}>
              <option value="">All Categories</option>
              <option value="Admin">Admin</option>
              <option value="Game Planner">Game Planner</option>
              <option value="Attendance">Attendance</option>
              <option value="Court Planner">Court Planner</option>
              <option value="Payroll">Payroll</option>
            </select>
            <button onClick={() => loadAuditLogs(auditPage)} className="btn-submit" style={{ width: 'auto', padding: '6px 15px', marginTop: 0 }}>Refresh</button>
          </div>
          {auditLoading ? (
            <div className="loading">Loading audit logs...</div>
          ) : (
            <>
              <p style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>
                Showing {auditLogs.length} of {auditTotal} entries (Page {auditPage} of {Math.ceil(auditTotal / auditPageSize) || 1})
              </p>
              <table className="admin-table">
                <thead><tr><th>Timestamp (AEST)</th><th>User</th><th>Action</th><th>Category</th><th>Details</th><th>From</th><th>To</th></tr></thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr><td colSpan="7" style={{ textAlign: 'center', color: '#999' }}>No audit logs found</td></tr>
                  ) : (
                    auditLogs.map(log => (
                      <tr key={log.id}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>{formatAuditTimestamp(log.timestamp)}</td>
                        <td style={{ fontSize: '12px' }}>{log.userName || '-'}<br/><span style={{ color: '#999' }}>{log.userEmail || ''}</span></td>
                        <td style={{ fontSize: '12px' }}>{log.action}</td>
                        <td style={{ fontSize: '12px' }}>{log.category}</td>
                        <td style={{ fontSize: '12px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.details}>{log.details || '-'}</td>
                        <td style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.oldValue}>{log.oldValue || '-'}</td>
                        <td style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.newValue}>{log.newValue || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'center' }}>
                <button onClick={() => loadAuditLogs(auditPage - 1)} disabled={auditPage <= 1} className="btn-submit" style={{ width: 'auto', padding: '6px 15px', marginTop: 0 }}>Previous</button>
                <span style={{ lineHeight: '32px' }}>Page {auditPage} of {Math.ceil(auditTotal / auditPageSize) || 1}</span>
                <button onClick={() => loadAuditLogs(auditPage + 1)} disabled={auditPage >= Math.ceil(auditTotal / auditPageSize)} className="btn-submit" style={{ width: 'auto', padding: '6px 15px', marginTop: 0 }}>Next</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* COACHING COURTS TAB */}
      {activeTab === 'coaching-courts' && canView('admin', 'program-types') && (
        <div className="admin-section">
          {showCourtForm && canEdit('admin', 'program-types') && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowCourtForm(false); setEditingCourtId(null); setCourtForm({ name: '' }); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '400px', width: '90%' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingCourtId ? 'Edit Court' : 'Add New Court'}</h3>
                  <button onClick={() => { setShowCourtForm(false); setEditingCourtId(null); setCourtForm({ name: '' }); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddCourt} className="admin-form">
                  <div className="form-group">
                    <label>Court Name:</label>
                    <input type="text" placeholder="e.g., Court 5" value={courtForm.name} onChange={(e) => setCourtForm({ name: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn-submit">{editingCourtId ? 'Update Court' : 'Add Court'}</button>
                    <button type="button" className="btn-cancel" onClick={() => { setShowCourtForm(false); setEditingCourtId(null); setCourtForm({ name: '' }); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedCourts} onChange={(e) => setShowArchivedCourts(e.target.checked)} />
              Show Archived
            </label>
            {canEdit('admin', 'program-types') && (
              <button onClick={() => { setEditingCourtId(null); setCourtForm({ name: '' }); setShowCourtForm(true); }} style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>+ Add Court</button>
            )}
          </div>
          <table className="admin-table">
            <thead><tr><th>Court Name</th>{canEdit('admin', 'program-types') && <th>Actions</th>}</tr></thead>
            <tbody>
              {coachingCourts.map(c => (
                <tr key={c.id} style={c.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td>{c.name}{c.isArchived ? ' (Archived)' : ''}</td>
                  {canEdit('admin', 'program-types') && (
                    <td>
                      {!c.isArchived && <button className="btn-edit" onClick={() => { setCourtForm({ name: c.name }); setEditingCourtId(c.id); setShowCourtForm(true); }}>Edit</button>}
                      {!c.isArchived && <button className="btn-delete" onClick={() => handleArchiveCourt(c.id)}>Archive</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PROGRAM TYPES TAB */}
      {activeTab === 'program-types' && canView('admin', 'program-types') && (
        <div className="admin-section">
          {showProgramTypeForm && canEdit('admin', 'program-types') && (
            <CoachingProgramForm
              programId={editingProgramTypeId}
              onCancel={() => { setShowProgramTypeForm(false); setEditingProgramTypeId(null); }}
              onSaved={(result) => {
                setSuccess(result.message);
                setShowProgramTypeForm(false);
                setEditingProgramTypeId(null);
                loadAllData();
              }}
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedProgramTypes} onChange={(e) => setShowArchivedProgramTypes(e.target.checked)} />
              Show Archived
            </label>
            {canEdit('admin', 'program-types') && (
              <button onClick={() => { setEditingProgramTypeId(null); setShowProgramTypeForm(true); }} style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>+ Add Coaching Program</button>
            )}
          </div>
          <table className="admin-table">
            <thead><tr><th>Name</th><th>Owners</th><th>Dates</th><th>Sessions/wk</th><th>Weekly Budget</th><th>Linked Programs</th><th>Signup URL</th>{canEdit('admin', 'program-types') && <th>Actions</th>}</tr></thead>
            <tbody>
              {programTypes.map(pt => {
                const feederNames = (pt.feederProgramTypeIds || [])
                  .map(fid => programTypes.find(o => o.id === fid)?.name)
                  .filter(Boolean);
                return (
                <tr key={pt.id} style={pt.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td>{pt.name}{pt.isArchived ? ' (Archived)' : ''}</td>
                  <td>{pt.ownerName || '-'}</td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {pt.startDate || pt.endDate
                      ? `${pt.startDate ? String(pt.startDate).split('T')[0] : '?'} → ${pt.endDate ? String(pt.endDate).split('T')[0] : '?'}`
                      : '-'}
                  </td>
                  <td style={{ fontSize: '0.85rem' }} title={(pt.schedule || []).map(s => `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][s.dayOfWeek]} ${s.startTime} (${s.durationMinutes}m${s.court ? `, ${s.court}` : ''})`).join('\n')}>
                    {(pt.schedule || []).length || '-'}
                  </td>
                  <td>{pt.weeklyBudget ? `$${parseFloat(pt.weeklyBudget).toFixed(2)}` : '-'}</td>
                  <td style={{ fontSize: '0.85rem', maxWidth: 220 }} title={feederNames.join(', ')}>
                    {feederNames.length > 0 ? feederNames.join(', ') : '-'}
                  </td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {pt.signupUrl ? <a href={pt.signupUrl} target="_blank" rel="noopener noreferrer" title={pt.signupUrl}>{pt.signupUrl}</a> : '-'}
                  </td>
                  {canEdit('admin', 'program-types') && (
                    <td>
                      {!pt.isArchived && <button className="btn-edit" onClick={() => handleEditProgramType(pt)}>Edit</button>}
                      {!pt.isArchived && <button className="btn-delete" onClick={() => handleArchiveProgramType(pt.id)}>Archive</button>}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* WEEKLY TEMPLATES TAB removed — manual weekly templates are
          deprecated; the BC integration mapping flow now creates the
          underlying ProgramTypes + WeeklyEventTemplates + Programs in one
          shot, with default coaches and a recurrence range. */}

      {/* SECURITY PROFILES TAB */}
      {activeTab === 'security-profiles' && canView('admin', 'security-profiles') && (
        <div className="admin-section">
          {showProfileForm && canEdit('admin', 'security-profiles') && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowProfileForm(false); setEditingProfileId(null); setProfileForm({ name: '', permissions: {} }); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '800px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingProfileId ? 'Edit Security Profile' : 'Add New Security Profile'}</h3>
                  <button onClick={() => { setShowProfileForm(false); setEditingProfileId(null); setProfileForm({ name: '', permissions: {} }); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddProfile} className="admin-form">
                  <div className="form-group">
                    <label>Profile Name:</label>
                    <input type="text" placeholder="e.g., Full Admin, Viewer, Coordinator" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} required />
                  </div>
                  {profileForm.permissions && Object.keys(profileForm.permissions).length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <h4 style={{ marginBottom: '10px' }}>Permissions</h4>
                      {(() => {
                          // Group permissions by pageLabel preserving the
                          // catalog's first-seen order. Renders a heading
                          // row per group with a "Set all to" trio so an
                          // admin can grant/revoke a whole page in one
                          // click without ticking each section.
                          const groups = [];
                          const groupIdx = new Map();
                          for (const perm of ALL_PERMISSIONS) {
                              const key = perm.pageLabel || '(other)';
                              if (!groupIdx.has(key)) {
                                  groupIdx.set(key, groups.length);
                                  groups.push({ label: key, perms: [] });
                              }
                              groups[groupIdx.get(key)].perms.push(perm);
                          }
                          const setGroup = (perms, level) => {
                              setProfileForm(prev => {
                                  const next = { ...prev.permissions };
                                  for (const p of perms) {
                                      next[p.pageKey] = { ...(next[p.pageKey] || {}), [p.sectionKey]: level };
                                  }
                                  return { ...prev, permissions: next };
                              });
                          };
                          return (
                              <table className="admin-table" style={{ fontSize: '13px' }}>
                                  <thead>
                                      <tr>
                                          <th style={{ textAlign: 'left' }}>Section</th>
                                          <th style={{ textAlign: 'center', width: '80px' }}>Edit</th>
                                          <th style={{ textAlign: 'center', width: '80px' }}>View</th>
                                          <th style={{ textAlign: 'center', width: '80px' }}>Hide</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      {groups.map((g) => (
                                          <React.Fragment key={g.label}>
                                              <tr style={{ background: '#e3f2fd' }}>
                                                  <td style={{ fontWeight: 700, color: '#0d47a1' }}>
                                                      {g.label}
                                                  </td>
                                                  <td style={{ textAlign: 'center' }}>
                                                      <button type="button" title={`Grant Edit on every ${g.label} section`} onClick={() => setGroup(g.perms, 'edit')} style={{ fontSize: 11, padding: '2px 6px', cursor: 'pointer' }}>all</button>
                                                  </td>
                                                  <td style={{ textAlign: 'center' }}>
                                                      <button type="button" title={`Grant View on every ${g.label} section`} onClick={() => setGroup(g.perms, 'view')} style={{ fontSize: 11, padding: '2px 6px', cursor: 'pointer' }}>all</button>
                                                  </td>
                                                  <td style={{ textAlign: 'center' }}>
                                                      <button type="button" title={`Hide every ${g.label} section`} onClick={() => setGroup(g.perms, 'hide')} style={{ fontSize: 11, padding: '2px 6px', cursor: 'pointer' }}>all</button>
                                                  </td>
                                              </tr>
                                              {g.perms.map((perm) => (
                                                  <tr key={`${perm.pageKey}-${perm.sectionKey}`}>
                                                      <td style={{ paddingLeft: 24 }}>{perm.label}</td>
                                                      <td style={{ textAlign: 'center' }}>
                                                          <input type="radio" name={`perm-${perm.pageKey}-${perm.sectionKey}`} checked={profileForm.permissions[perm.pageKey]?.[perm.sectionKey] === 'edit'} onChange={() => setPermLevel(perm.pageKey, perm.sectionKey, 'edit')} />
                                                      </td>
                                                      <td style={{ textAlign: 'center' }}>
                                                          <input type="radio" name={`perm-${perm.pageKey}-${perm.sectionKey}`} checked={profileForm.permissions[perm.pageKey]?.[perm.sectionKey] === 'view'} onChange={() => setPermLevel(perm.pageKey, perm.sectionKey, 'view')} />
                                                      </td>
                                                      <td style={{ textAlign: 'center' }}>
                                                          <input type="radio" name={`perm-${perm.pageKey}-${perm.sectionKey}`} checked={profileForm.permissions[perm.pageKey]?.[perm.sectionKey] === 'hide'} onChange={() => setPermLevel(perm.pageKey, perm.sectionKey, 'hide')} />
                                                      </td>
                                                  </tr>
                                              ))}
                                          </React.Fragment>
                                      ))}
                                  </tbody>
                              </table>
                          );
                      })()}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                    <button type="submit" className="btn-submit">{editingProfileId ? 'Update Profile' : 'Create Profile'}</button>
                    {!editingProfileId && (
                      <button type="button" className="btn-submit" style={{ background: '#666' }} onClick={() => setProfileForm({ name: profileForm.name || '', permissions: initProfilePermissions() })}>Setup Permissions</button>
                    )}
                    <button type="button" className="btn-delete" onClick={() => { setShowProfileForm(false); setEditingProfileId(null); setProfileForm({ name: '', permissions: {} }); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedProfiles} onChange={(e) => setShowArchivedProfiles(e.target.checked)} />
              Show Archived
            </label>
            {canEdit('admin', 'security-profiles') && (
              <button onClick={() => { setEditingProfileId(null); setProfileForm({ name: '', permissions: {} }); setShowProfileForm(true); }} style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>+ Add Security Profile</button>
            )}
          </div>
          <table className="admin-table">
            <thead><tr><th>Profile Name</th><th>Permissions Count</th>{canEdit('admin', 'security-profiles') && <th>Actions</th>}</tr></thead>
            <tbody>
              {securityProfiles.map(profile => (
                <tr key={profile.id} style={profile.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td>{profile.name}{profile.isArchived ? ' (Archived)' : ''}</td>
                  <td>{profile.permissions?.filter(p => p.permissionLevel !== 'hide').length || 0} active</td>
                  {canEdit('admin', 'security-profiles') && (
                    <td>
                      {!profile.isArchived && <button className="btn-edit" onClick={() => handleEditProfile(profile)}>Edit</button>}
                      {!profile.isArchived && <button className="btn-delete" onClick={() => handleArchiveProfile(profile.id)}>Archive</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SITE PERMISSIONS TAB */}
      {activeTab === 'roster-response' && canView('admin', 'roster-response') && (
        <div className="admin-section">
          <div className="form-container">
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Enable or disable accept/reject buttons for each roster type in My Portal.
              When enabled, the cutoff hours control how long before a shift the buttons become locked.
            </p>
            <div className="admin-form">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: rosterCutoff.gameEnabled ? '#e8f5e9' : '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, minWidth: '200px' }}>
                    <input type="checkbox" checked={rosterCutoff.gameEnabled} onChange={(e) => setRosterCutoff({ ...rosterCutoff, gameEnabled: e.target.checked })} disabled={!canEdit('admin', 'roster-response')} />
                    Game Roster Response
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: rosterCutoff.gameEnabled ? 1 : 0.4 }}>
                    <label style={{ fontSize: '0.85rem', color: '#666' }}>Cutoff:</label>
                    <input type="number" min="0" style={{ width: '70px' }} value={rosterCutoff.game} onChange={(e) => setRosterCutoff({ ...rosterCutoff, game: parseInt(e.target.value) || 0 })} disabled={!canEdit('admin', 'roster-response') || !rosterCutoff.gameEnabled} />
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>hours</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: rosterCutoff.coachEnabled ? '#e8f5e9' : '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, minWidth: '200px' }}>
                    <input type="checkbox" checked={rosterCutoff.coachEnabled} onChange={(e) => setRosterCutoff({ ...rosterCutoff, coachEnabled: e.target.checked })} disabled={!canEdit('admin', 'roster-response')} />
                    Coach Roster Response
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: rosterCutoff.coachEnabled ? 1 : 0.4 }}>
                    <label style={{ fontSize: '0.85rem', color: '#666' }}>Cutoff:</label>
                    <input type="number" min="0" style={{ width: '70px' }} value={rosterCutoff.coach} onChange={(e) => setRosterCutoff({ ...rosterCutoff, coach: parseInt(e.target.value) || 0 })} disabled={!canEdit('admin', 'roster-response') || !rosterCutoff.coachEnabled} />
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>hours</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: rosterCutoff.courtEnabled ? '#e8f5e9' : '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, minWidth: '200px' }}>
                    <input type="checkbox" checked={rosterCutoff.courtEnabled} onChange={(e) => setRosterCutoff({ ...rosterCutoff, courtEnabled: e.target.checked })} disabled={!canEdit('admin', 'roster-response')} />
                    Court Roster Response
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: rosterCutoff.courtEnabled ? 1 : 0.4 }}>
                    <label style={{ fontSize: '0.85rem', color: '#666' }}>Cutoff:</label>
                    <input type="number" min="0" style={{ width: '70px' }} value={rosterCutoff.court} onChange={(e) => setRosterCutoff({ ...rosterCutoff, court: parseInt(e.target.value) || 0 })} disabled={!canEdit('admin', 'roster-response') || !rosterCutoff.courtEnabled} />
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>hours</span>
                  </div>
                </div>
              </div>
              {canEdit('admin', 'roster-response') && (
                <button type="button" className="btn-submit" onClick={handleSaveRosterCutoff}>Save Settings</button>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'site-permissions' && canView('admin', 'site-permissions') && (
        <div className="admin-section">
          {showPermissionForm && canEdit('admin', 'site-permissions') && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowPermissionForm(false); setUserPermForm({ userEmail: '', securityProfileId: '' }); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>Assign User Permission</h3>
                  <button onClick={() => { setShowPermissionForm(false); setUserPermForm({ userEmail: '', securityProfileId: '' }); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAssignPermission} className="admin-form">
                  <div className="form-group">
                    <label>User Email:</label>
                    <input type="email" placeholder="user@organization.com" value={userPermForm.userEmail} onChange={(e) => setUserPermForm({ ...userPermForm, userEmail: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Security Profile:</label>
                    <select value={userPermForm.securityProfileId} onChange={(e) => setUserPermForm({ ...userPermForm, securityProfileId: e.target.value })} required>
                      <option value="">Select Profile</option>
                      {securityProfiles.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn-submit">Assign Permission</button>
                    <button type="button" className="btn-cancel" onClick={() => { setShowPermissionForm(false); setUserPermForm({ userEmail: '', securityProfileId: '' }); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1rem' }}>
            {canEdit('admin', 'site-permissions') && (
              <button onClick={() => { setUserPermForm({ userEmail: '', securityProfileId: '' }); setShowPermissionForm(true); }} style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>+ Add Permission</button>
            )}
          </div>
          <table className="admin-table">
            <thead><tr><th>Email</th><th>Security Profile</th><th>Assigned</th>{canEdit('admin', 'site-permissions') && <th>Actions</th>}</tr></thead>
            <tbody>
              {userPermissions.map(up => (
                <tr key={up.id}>
                  <td>{up.userEmail}</td>
                  <td>
                    {canEdit('admin', 'site-permissions') ? (
                      <select value={up.securityProfileId} onChange={(e) => handleChangeUserProfile(up.userEmail, e.target.value)}>
                        {securityProfiles.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
                      </select>
                    ) : up.profileName}
                  </td>
                  <td>{new Date(up.assignedAt).toLocaleString('en-AU')}</td>
                  {canEdit('admin', 'site-permissions') && (
                    <td><button className="btn-delete" onClick={() => handleRemoveUserPermission(up.userEmail)}>Remove</button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'business-banking' && canView('admin', 'business-banking') && (
        <div className="admin-section">
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Configure the organisation's banking details for payroll file generation.
            </p>
            <div className="form-group">
              <label>BSB Number</label>
              <input type="text" value={businessBanking.bsbNumber} onChange={e => setBusinessBanking({...businessBanking, bsbNumber: e.target.value})} disabled={!canEdit('admin', 'business-banking')} />
            </div>
            <div className="form-group">
              <label>Account Number</label>
              <input type="text" value={businessBanking.accountNumber} onChange={e => setBusinessBanking({...businessBanking, accountNumber: e.target.value})} disabled={!canEdit('admin', 'business-banking')} />
            </div>
            <div className="form-group">
              <label>Account Name</label>
              <input type="text" value={businessBanking.accountName} onChange={e => setBusinessBanking({...businessBanking, accountName: e.target.value})} disabled={!canEdit('admin', 'business-banking')} />
            </div>
            <div className="form-group">
              <label>Financial Institution Code</label>
              <input type="text" value={businessBanking.financialInstitutionCode} onChange={e => setBusinessBanking({...businessBanking, financialInstitutionCode: e.target.value})} disabled={!canEdit('admin', 'business-banking')} />
            </div>
            <div className="form-group">
              <label>Lodgement Reference</label>
              <input type="text" value={businessBanking.lodgementReference} onChange={e => setBusinessBanking({...businessBanking, lodgementReference: e.target.value})} disabled={!canEdit('admin', 'business-banking')} />
            </div>
            <div className="form-group">
              <label>Name Of User Supplying File</label>
              <input type="text" value={businessBanking.nameOfUserSupplyingFile} onChange={e => setBusinessBanking({...businessBanking, nameOfUserSupplyingFile: e.target.value})} disabled={!canEdit('admin', 'business-banking')} />
            </div>
            <div className="form-group">
              <label>APCA Number</label>
              <input type="text" value={businessBanking.apcaNumber} onChange={e => setBusinessBanking({...businessBanking, apcaNumber: e.target.value})} disabled={!canEdit('admin', 'business-banking')} />
            </div>
            <button onClick={handleSaveBusinessBanking} className="btn-submit" disabled={!canEdit('admin', 'business-banking')} style={{ marginTop: '20px' }}>
              Save Business Banking Details
            </button>
          </div>
        </div>
      )}

      {activeTab === 'sms' && canView('admin', 'sms') && (
        <AdminSms />
      )}

      {activeTab === 'bc-integration' && canView('admin', 'settings') && (
        <>
          <div role="alert" style={{ background: '#c62828', color: 'white', borderRadius: 8, padding: '1rem 1.25rem', margin: '0 0 1.25rem', fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.4, display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <span aria-hidden="true" style={{ fontSize: '1.8rem' }}>⚠</span>
            <span>
              Do not touch settings here unless integration with BasketballConnect has failed for multiple people.
              Check with the admin first.
            </span>
          </div>
          <AdminBCIntegration />
        </>
      )}

      {activeTab === 'competition-mapping' && canView('admin', 'competition-mapping') && (
        <AdminCompetitionMapping />
      )}

      {activeTab === 'error-log' && canView('admin', 'error-log') && (
        <AdminErrorLog />
      )}

      {activeTab === 'qualifications' && (
        <Qualifications />
      )}

      {activeTab === 'referee-codes' && canView('admin', 'referee-codes') && (
        <AdminRefereeCodes canEdit={canEdit('admin', 'referee-codes')} />
      )}
      {activeTab === 'swishiq' && canView('admin', 'settings') && (
        <AdminSwishIq canEdit={canEdit('admin', 'settings')} />
      )}
      {activeTab === 'coach-codes' && canView('admin', 'coach-codes') && (
        <AdminRefereeCodes kind="coach" canEdit={canEdit('admin', 'coach-codes')} />
      )}

      {activeTab === 'ai-management' && canView('admin', 'ai-management') && (
        <AdminAIManagement canEdit={canEdit('admin', 'ai-management')} />
      )}

      {activeTab === 'reporting' && canView('admin', 'reporting') && (
        <AdminReporting canEdit={canEdit('admin', 'reporting')} />
      )}

    </div>
  );
}
